import { memoize } from 'lodash'
import { APIConfig, Id } from 'types/definitions'

import { enableDevTools } from 'utils/grpc-util'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import { TaxRefundMethodServicePromiseClient } from '@taxfyle/api-internal/internal/tax_refund_methods_grpc_web_pb'
import {
  CreateTaxRefundMethodRequest,
  GetBusinessTaxRefundMethodForJobRequest,
  GetPersonalTaxRefundMethodForJobRequest,
  UpdateTaxRefundMethodRequest,
} from '@taxfyle/api-internal/internal/tax_refund_methods_pb'

export function createTaxRefundMethodsAPI(config: APIConfig) {
  return new TaxRefundMethodsAPI(config)
}

export class TaxRefundMethodsAPI {
  private readonly taxRefundMethodsClient = createTaxRefundMethodsClient(
    this.config.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(this.config)

  constructor(private config: APIConfig) {}

  /**
   * Gets business tax refund method for a given job ID.
   */
  async getBusinessTaxRefundMethodForJob(jobId: Id) {
    const req = new GetBusinessTaxRefundMethodForJobRequest().setJobId(
      Id.assert(jobId),
    )

    return this.taxRefundMethodsClient
      .getBusinessTaxRefundMethodForJob(
        req,
        await this.grpcAuthenticator.getMeta(),
      )
      .then((res) => res.toObject())
  }

  /**
   * Gets personal tax refund method for a given job ID.
   */
  async getPersonalTaxRefundMethodForJob(jobId: Id) {
    const req = new GetPersonalTaxRefundMethodForJobRequest().setJobId(
      Id.assert(jobId),
    )

    return this.taxRefundMethodsClient
      .getPersonalTaxRefundMethodForJob(
        req,
        await this.grpcAuthenticator.getMeta(),
      )
      .then((res) => res.toObject())
  }

  /**
   * Creates a new tax refund method.
   */
  async createTaxRefundMethod(request: CreateTaxRefundMethodRequest) {
    return this.taxRefundMethodsClient
      .createTaxRefundMethod(request, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }

  /**
   * Updates an existing tax refund method.
   */
  async updateTaxRefundMethod(request: UpdateTaxRefundMethodRequest) {
    return this.taxRefundMethodsClient
      .updateTaxRefundMethod(request, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }
}

/**
 * Creates a tax refund methods client that is memoized.
 */
const createTaxRefundMethodsClient = memoize(
  function createTaxRefundMethodsGrpcClient(baseUrl: string) {
    const client = new TaxRefundMethodServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)
